/* eslint-disable @typescript-eslint/no-explicit-any */
import { ActivityBannerComponent } from "@/types/compontent/comp/activityBanner.ts";

import { BaseComponent } from "./base/baseComponent";
import { CompType } from "./base/baseEnum";
import { BannerComponent } from "./comp/banner";
import { BetGameComponent } from "./comp/betGame";
import { BetRecordComponent } from "./comp/betRecord";
import { CarouselComponent } from "./comp/carousel";
import { CompetitionComponent } from "./comp/competition";
import { CustomListComponent } from "./comp/customList";
import { FootInfoComponent } from "./comp/footInfo";
import { GameEntryComponent } from "./comp/gameEntry";
import { GameListComponent } from "./comp/gameList";
import { GameNavComponent } from "./comp/gameNav";
import { GuessYouLikeComponent } from "./comp/guessYouLike";
import { HotColdGameComponent } from "./comp/hotColdGame";
import { MakerListComponent } from "./comp/makerList";
import { MarqueeComponent } from "./comp/marquee";
import { MediaComponent } from "./comp/media";
import { PolicyComponent } from "./comp/policy";
import { PromoteComponent } from "./comp/promote";
import { SearchComponent } from "./comp/search";
import { SeoComponent } from "./comp/seo";
import { SportComponent } from "./comp/sport";
import { StochasticGameComponent } from "./comp/stochasticGame";
import { StoreDownloadComponent } from "./comp/storeDownload";
import { TournamentComponent } from "./comp/tournament";
import { WinnerComponent } from "./comp/winner";

export class CompontentFactory {
  private static instance: CompontentFactory;
  private components: Map<string, BaseComponent<any>> = new Map();

  private constructor() {}

  public static getInstance(): CompontentFactory {
    if (!CompontentFactory.instance) {
      CompontentFactory.instance = new CompontentFactory();
    }
    return CompontentFactory.instance;
  }

  /**
   * 根据组件类型创建对应的组件实例
   *
   * @param config 组件配置或组件实例
   * @returns 组件实例
   */
  public createComponent(config: any): BaseComponent<any> {
    if (!config) {
      throw new Error("Component config is required");
    }
    if (!config.key) {
      throw new Error("Component key is required");
    }

    // 如果传入的是组件实例，直接返回
    if (config instanceof BaseComponent) {
      const key = config.key || `${config.type}_${Date.now()}`;
      if (!config.key) {
        config.key = key;
      }
      this.components.set(key, config);
      return config;
    }

    if (!config.type) {
      throw new Error("Component type is required");
    }

    const key = config.key || `${config.type}_${Date.now()}`;
    config.key = key;

    const component = this.createNewComponent(config);
    this.components.set(key, component);
    return component;
  }

  /**
   * 创建新的组件实例
   *
   * @param config 组件配置
   * @returns 组件实例
   */
  private createNewComponent(config: any): BaseComponent<any> {
    const type = config.type;
    let component: BaseComponent<any>;

    switch (type) {
      case CompType.banner:
        component = new BannerComponent(config);
        break;
      // case CompType.banner_s:
      //   component = new BannerComponent(config);
      //   break;
      case CompType.carousel:
        component = new CarouselComponent(config);
        break;
      // case CompType.activity:
      //   component = new ActivityComponent(config);
      //   break;
      case CompType.marquee:
        component = new MarqueeComponent(config);
        break;
      case CompType.game_nav:
        component = new GameNavComponent(config);
        break;
      case CompType.bet_game:
        component = new BetGameComponent(config);
        break;
      case CompType.sport:
        component = new SportComponent(config);
        break;
      case CompType.search:
        component = new SearchComponent(config);
        break;
      case CompType.foot_info:
        component = new FootInfoComponent(config);
        break;
      case CompType.game_list:
        component = new GameListComponent(config);
        break;
      case CompType.maker_list:
        component = new MakerListComponent(config);
        break;
      case CompType.game_entry:
        component = new GameEntryComponent(config);
        break;
      // case CompType.pay:
      //   component = new PayComponent(config);
      //   break;
      case CompType.winner:
        component = new WinnerComponent(config);
        break;
      case CompType.bet_record:
        component = new BetRecordComponent(config);
        break;
      case CompType.media:
        component = new MediaComponent(config);
        break;
      case CompType.policy:
        component = new PolicyComponent(config);
        break;
      case CompType.guess_you_like:
        component = new GuessYouLikeComponent(config);
        break;
      case CompType.custom_list:
        component = new CustomListComponent(config);
        break;
      case CompType.stochastic_game:
        component = new StochasticGameComponent(config);
        break;
      case CompType.promote:
        component = new PromoteComponent(config);
        break;
      case CompType.hot_cold_game:
        component = new HotColdGameComponent(config);
        break;
      case CompType.seo:
        component = new SeoComponent(config);
        break;
      case CompType.champion_ship: // tournament
        component = new TournamentComponent(config);
        break;
      case CompType.activity_banner:
        component = new ActivityBannerComponent(config);
        break;
      case CompType.store_download:
        component = new StoreDownloadComponent(config);
        break;
      case CompType.competition:
        component = new CompetitionComponent(config);
        break;
      default:
        throw new Error(`Unsupported component type: ${type}`);
    }

    return component;
  }

  /**
   * 批量创建组件
   *
   * @param configs 组件配置数组或组件实例数组
   * @returns 组件实例数组
   */
  public createComponents(
    configs: Array<Partial<BaseComponent<any>> & { type: CompType }>,
  ): BaseComponent<any>[] {
    return configs.map((config) => this.createComponent(config));
  }

  /**
   * 销毁单个组件
   *
   * @param component 要销毁的组件
   */
  public destroyComponent(component: BaseComponent<any>): void {
    if (!component) return;

    const key = component.key;
    if (key && this.components.has(key)) {
      component.destroy();
      this.components.delete(key);
    }
  }

  /** 销毁所有组件 */
  public destroyAllComponents(): void {
    this.components.forEach((component) => component.destroy());
    this.components.clear();
  }

  /** 获取当前所有组件 */
  public getComponents(): BaseComponent<any>[] {
    return Array.from(this.components.values());
  }

  /**
   * 根据key获取组件
   *
   * @param key 组件key
   * @returns 组件实例，如果不存在则返回undefined
   */
  public getComponentByKey(key: string): BaseComponent<any> | undefined {
    return this.components.get(key);
  }
}
